import type { CoworkFileCatalogFacet } from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { createEffect, createMemo, createUniqueId, For, on, onCleanup, onMount, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"

const fileCategories = ["all", "document", "spreadsheet", "pdf", "dataset"] as const
export type FileCategory = (typeof fileCategories)[number]

const documentTypeLabels: Record<string, string> = {
  invoice: "cowork.accounting.documentType.invoice",
  receipt: "cowork.accounting.documentType.receipt",
  bank_statement: "cowork.accounting.documentType.bank_statement",
  payroll: "cowork.enterprise.files.filters.payroll",
  unknown: "cowork.enterprise.files.filters.unknownType",
}

export function documentTypeLabelKey(type?: string) {
  return documentTypeLabels[type ?? "unknown"] ?? documentTypeLabels.unknown
}

export function documentTypeLabel(type: string | undefined, translate: (key: string) => string) {
  return type && !documentTypeLabels[type] ? type.replaceAll("_", " ") : translate(documentTypeLabelKey(type))
}

export function FileCatalogFilters(props: {
  scopeKey: string
  category: FileCategory
  onCategory: (category: FileCategory) => void
  accounting: boolean
  children?: JSX.Element
  sellers: CoworkFileCatalogFacet[]
  documentTypes: CoworkFileCatalogFacet[]
  selectedSellers: string[]
  selectedTypes: string[]
  onSellers: (ids: string[]) => void
  onTypes: (types: string[]) => void
  onClear: () => void
  canClear: boolean
  shown: number
  total: number
}) {
  const language = useLanguage()
  const [state, setState] = createStore({ sellerSearch: "" })
  const categoryGroup = createUniqueId()
  let categoryMenu: HTMLDetailsElement | undefined
  let sellerMenu: HTMLDetailsElement | undefined
  let typeMenu: HTMLDetailsElement | undefined
  const closeMenus = () => {
    if (categoryMenu) categoryMenu.open = false
    if (sellerMenu) sellerMenu.open = false
    if (typeMenu) typeMenu.open = false
  }
  onMount(() => {
    const dismissOutside = (event: Event) => {
      const target = event.target
      if (!(target instanceof Node)) return
      for (const menu of [categoryMenu, sellerMenu, typeMenu]) {
        if (menu?.open && !menu.contains(target)) menu.open = false
      }
    }
    const dismissEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      const menu = [categoryMenu, sellerMenu, typeMenu].find((item) => item?.open)
      if (!menu) return
      event.preventDefault()
      event.stopPropagation()
      closeMenus()
      menu.querySelector("summary")?.focus()
    }
    document.addEventListener("click", dismissOutside, true)
    document.addEventListener("focusin", dismissOutside, true)
    document.addEventListener("keydown", dismissEscape, true)
    onCleanup(() => {
      document.removeEventListener("click", dismissOutside, true)
      document.removeEventListener("focusin", dismissOutside, true)
      document.removeEventListener("keydown", dismissEscape, true)
    })
  })
  const knownSellers = new Map<string, CoworkFileCatalogFacet>()
  createEffect(
    on(
      () => props.scopeKey,
      (key, previous) => {
        if (key === previous) return
        knownSellers.clear()
        closeMenus()
        setState("sellerSearch", "")
      },
    ),
  )
  createEffect(() => props.sellers.forEach((seller) => knownSellers.set(seller.id, seller)))
  const sellerLabel = (seller: CoworkFileCatalogFacet) =>
    seller.id === "__not_extracted__"
      ? language.t("cowork.enterprise.files.filters.notExtracted")
      : seller.id === "__unknown__"
        ? language.t("cowork.enterprise.files.filters.unknownSeller")
        : seller.label
  const typeLabel = (type: string) => documentTypeLabel(type, language.t)
  const visibleSellers = createMemo(() => {
    const search = state.sellerSearch.trim().toLowerCase()
    return props.sellers.filter((seller) =>
      `${sellerLabel(seller)} ${seller.vat_number ?? ""}`.toLowerCase().includes(search),
    )
  })
  const toggle = (values: string[], id: string, checked: boolean) =>
    checked ? [...new Set([...values, id])] : values.filter((value) => value !== id)
  const chips = createMemo(() => [
    ...props.selectedSellers.map((id) => ({
      id: `seller:${id}`,
      label: sellerLabel(
        props.sellers.find((seller) => seller.id === id) ??
          knownSellers.get(id) ?? { id, label: language.t("cowork.accounting.extractions.party.seller"), count: 0 },
      ),
      remove: () => props.onSellers(props.selectedSellers.filter((value) => value !== id)),
    })),
    ...props.selectedTypes.map((id) => ({
      id: `type:${id}`,
      label: typeLabel(id),
      remove: () => props.onTypes(props.selectedTypes.filter((value) => value !== id)),
    })),
  ])
  return (
    <div class="mb-4 space-y-3 text-[12px]">
      <div class="flex flex-wrap items-start gap-2">
        <details ref={(element) => (categoryMenu = element)} class="relative">
          <summary class="cursor-pointer rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
            {language.t("cowork.enterprise.files.filters.fileType")}
            <Show when={props.category !== "all"}>
              <span class="ml-2">({language.t(`cowork.enterprise.files.filter.${props.category}`)})</span>
            </Show>
          </summary>
          <div class="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-2 shadow-[var(--v2-elevation-raised)]">
            <For each={fileCategories}>
              {(category) => (
                <label class="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-v2-overlay-simple-overlay-hover">
                  <input
                    type="radio"
                    name={categoryGroup}
                    value={category}
                    checked={props.category === category}
                    onChange={() => props.onCategory(category)}
                  />
                  <span class="flex-1">{language.t(`cowork.enterprise.files.filter.${category}`)}</span>
                </label>
              )}
            </For>
          </div>
        </details>
        <Show when={props.accounting}>
          <details ref={(element) => (sellerMenu = element)} class="relative">
            <summary class="cursor-pointer rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
              {language.t("cowork.accounting.extractions.party.seller")}
              <Show when={props.selectedSellers.length}>
                <span class="ml-2">({props.selectedSellers.length})</span>
              </Show>
            </summary>
            <div class="absolute left-0 top-full z-30 mt-1 w-80 max-w-[85vw] rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 shadow-[var(--v2-elevation-raised)]">
              <input
                class="mb-2 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-2 outline-none"
                aria-label={language.t("cowork.enterprise.files.filters.searchSellers")}
                placeholder={language.t("cowork.enterprise.files.filters.searchSellers")}
                value={state.sellerSearch}
                onInput={(event) => setState("sellerSearch", event.currentTarget.value)}
              />
              <div class="max-h-64 space-y-1 overflow-auto">
                <For
                  each={visibleSellers()}
                  fallback={
                    <p class="p-2 text-v2-text-text-muted">
                      {language.t("cowork.accounting.documents.noFilterValues")}
                    </p>
                  }
                >
                  {(seller) => (
                    <label class="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-v2-overlay-simple-overlay-hover">
                      <input
                        type="checkbox"
                        checked={props.selectedSellers.includes(seller.id)}
                        onChange={(event) =>
                          props.onSellers(toggle(props.selectedSellers, seller.id, event.currentTarget.checked))
                        }
                      />
                      <span class="min-w-0 flex-1 break-words">
                        {sellerLabel(seller)}
                        <Show when={seller.vat_number}>
                          <span class="block text-[10px] text-v2-text-text-muted">{seller.vat_number}</span>
                        </Show>
                      </span>
                      <span class="text-v2-text-text-muted">{seller.count}</span>
                    </label>
                  )}
                </For>
              </div>
            </div>
          </details>
        </Show>
        <Show when={props.accounting || props.documentTypes.some((type) => type.id !== "unknown" && type.count > 0)}>
          <details ref={(element) => (typeMenu = element)} class="relative">
            <summary class="cursor-pointer rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2">
              {language.t("cowork.accounting.extractions.fact.documentType")}
              <Show when={props.selectedTypes.length}>
                <span class="ml-2">({props.selectedTypes.length})</span>
              </Show>
            </summary>
            <div class="absolute left-0 top-full z-30 mt-1 w-64 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-2 shadow-[var(--v2-elevation-raised)]">
              <For each={props.documentTypes}>
                {(type) => (
                  <label class="flex cursor-pointer items-center gap-2 rounded p-2 hover:bg-v2-overlay-simple-overlay-hover">
                    <input
                      type="checkbox"
                      checked={props.selectedTypes.includes(type.id)}
                      onChange={(event) =>
                        props.onTypes(toggle(props.selectedTypes, type.id, event.currentTarget.checked))
                      }
                    />
                    <span class="flex-1">{typeLabel(type.id)}</span>
                    <span class="text-v2-text-text-muted">{type.count}</span>
                  </label>
                )}
              </For>
            </div>
          </details>
        </Show>
        <div class="w-full min-w-0 lg:w-[320px]">{props.children}</div>
        <span class="ml-auto py-2 text-v2-text-text-muted">
          {language.t("cowork.enterprise.files.filters.results", { shown: props.shown, total: props.total })}
        </span>
      </div>
      <Show when={props.canClear}>
        <div class="flex flex-wrap items-center gap-2">
          <For each={chips()}>
            {(chip) => (
              <button
                type="button"
                class="flex items-center gap-2 rounded-md bg-v2-background-bg-layer-03 px-2 py-1"
                aria-label={language.t("cowork.enterprise.files.filters.remove", { label: chip.label })}
                onClick={chip.remove}
              >
                {chip.label}
                <IconV2 name="close" size="small" />
              </button>
            )}
          </For>
          <button type="button" class="px-2 py-1 underline" onClick={props.onClear}>
            {language.t("cowork.enterprise.files.filters.clear")}
          </button>
        </div>
      </Show>
    </div>
  )
}
